import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type {
  DocumentSourceSlotProps,
  SelectedSpecificationDocument,
  SpecificationFieldValue,
  SpecificationRole,
  SpecificationSourceKind,
} from "./types";

export interface SpecificationFieldProps {
  role: SpecificationRole;
  value: SpecificationFieldValue;
  error?: string;
  disabled: boolean;
  documentOnly?: boolean;
  /** The textarea in text mode, the field group in document mode: what focus goes to when it is invalid. */
  controlRef: (element: HTMLElement | null) => void;
  onTextChange: (role: SpecificationRole, text: string) => void;
  onDocumentChange: (
    role: SpecificationRole,
    document: SelectedSpecificationDocument | null,
  ) => void;
  sourceOptions?: { document: boolean; derived: boolean };
  onSourceChange?: (
    role: SpecificationRole,
    kind: SpecificationSourceKind,
  ) => void;
  onEditCopy?: (
    role: SpecificationRole,
    document: SelectedSpecificationDocument,
    text: string,
  ) => void;
  renderDocumentSource?: (props: DocumentSourceSlotProps) => React.ReactNode;
}

const SpecificationField: React.FC<SpecificationFieldProps> = ({
  role,
  value,
  error,
  disabled,
  documentOnly = false,
  controlRef,
  onTextChange,
  onDocumentChange,
  renderDocumentSource,
  sourceOptions,
  onSourceChange,
  onEditCopy,
}) => {
  const { t } = useTranslation();
  const id = useId();
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const document =
    value.kind === "document"
      ? {
          documentId: value.documentId,
          versionId: value.versionId,
          title: value.title,
          fileName: value.fileName,
        }
      : null;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      ref={value.kind === "document" || documentOnly ? controlRef : undefined}
      tabIndex={value.kind === "document" || documentOnly ? -1 : undefined}
      aria-describedby={
        (value.kind === "document" || documentOnly) && error
          ? errorId
          : undefined
      }
      className="group/specification flex min-w-0 flex-col gap-1.5 rounded-md focus:outline-none"
    >
      <Label
        id={labelId}
        htmlFor={value.kind !== "document" && !documentOnly ? id : undefined}
        className={cn(
          "revy-cjk text-sm font-medium leading-[1.5]",
          error && "text-destructive",
        )}
      >
        {t(`drbfm.firstRunInput.fields.${role}`)}
      </Label>

      {renderDocumentSource && !documentOnly && (
        <div className="flex flex-wrap gap-2">
          {(["text", "document", "derived"] as const)
            .filter((kind) => kind === "text" || sourceOptions?.[kind])
            .map((kind) => (
              <Button
                key={kind}
                type="button"
                size="sm"
                variant={value.kind === kind ? "secondary" : "outline"}
                aria-pressed={value.kind === kind}
                disabled={disabled}
                onClick={() => onSourceChange?.(role, kind)}
              >
                {t(`drbfm.firstRunInput.sourceKinds.${kind}`)}
              </Button>
            ))}
        </div>
      )}
      {value.kind === "derived" && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t("drbfm.firstRunInput.derivedHelp", {
            name: value.origin.fileName || value.origin.title,
            version: value.origin.versionId,
          })}
        </p>
      )}
      {value.kind === "text" && renderDocumentSource && !documentOnly && (
        <p className="text-xs text-muted-foreground">
          {t("drbfm.firstRunInput.textHelp")}
        </p>
      )}
      <div
        className={cn(
          "relative rounded-lg group-focus-visible/specification:ring-2 group-focus-visible/specification:ring-ring group-focus-visible/specification:ring-offset-2",
          renderDocumentSource && !documentOnly && value.kind !== "document" &&
            "drbfm-specification-editor",
          value.kind !== "document" &&
            "rounded-lg border border-input bg-background focus-within:ring-2 focus-within:ring-ring",
          value.kind === "text" && error &&
            "border-destructive focus-within:ring-destructive",
        )}
      >
        {value.kind !== "document" && !documentOnly && (
          <Textarea
            id={id}
            ref={controlRef}
            value={value.text}
            onChange={(event) => onTextChange(role, event.target.value)}
            placeholder={t(`drbfm.firstRunInput.placeholders.${role}`)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            aria-required
            disabled={disabled}
            rows={renderDocumentSource ? 1 : 2}
            className={cn(
              "revy-cjk min-h-[70px] resize-y border-0 shadow-none focus-visible:ring-0",
              renderDocumentSource && "drbfm-specification-text",
            )}
          />
        )}

        {renderDocumentSource?.({
          role,
          value: document,
          onChange: (next) => onDocumentChange(role, next),
          onEditCopy:
            !documentOnly && onEditCopy
              ? (source, text) => onEditCopy(role, source, text)
              : undefined,
          disabled,
        })}
      </div>

      {error && (
        <p
          id={errorId}
          role="alert"
          className="revy-cjk text-xs font-medium leading-[1.7] text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default SpecificationField;
